/**
 * Teks "Log Percakapan" (v2/M6) lapis 2 (profil + daftar percakapan) dan
 * lapis 3 (transkrip). Lapis 1: `assistant-log.ts`.
 */
export interface AssistantLogDetailText {
	backToUsers: string;
	backToUser: string;
	breadcrumbRoot: string;
	profileVerified: string;
	profileUnverified: string;
	profileAssistantAllowed: string;
	profileAssistantBlocked: string;
	searchConversation: string;
	conversationsTitle: string;
	conversationsEmpty: string;
	conversationMeta: (
		date: string,
		from: string,
		to: string,
		messages: string,
	) => string;
	conversationVoice: string;
	conversationErrors: (n: string) => string;
	openConversation: string;
	loadMore: string;
	userNotFound: string;
	loadFailed: string;
	loading: string;
	/** Lapis 3 */
	transcriptMeta: (
		date: string,
		from: string,
		to: string,
		messages: string,
		models: string,
		errors: string,
	) => string;
	auditBanner: string;
	hideContent: string;
	showContent: string;
	contentHiddenNote: string;
	retentionNote: (days: number) => string;
	askedFrom: (route: string) => string;
	failoverChip: string;
	errorTurn: (reason: string) => string;
	errorTurnDefault: string;
	errorTurnNote: string;
	loadPrevious: (shown: string, total: string) => string;
	transcriptStart: string;
	infoTitle: string;
	infoUser: string;
	infoCreated: string;
	infoLastMessage: string;
	infoMessages: string;
	infoTurns: string;
	infoModalities: string;
	infoPageRoutes: string;
	infoModels: string;
	infoTools: string;
	infoTokens: string;
	infoCost: string;
	openAuditLog: string;
	turns: (n: string) => string;
	uses: (n: string) => string;
	none: string;
	transcriptNotFound: string;
	transcriptForbidden: string;
}

const id: AssistantLogDetailText = {
	backToUsers: "‹ Kembali ke daftar pengguna",
	backToUser: "‹ Kembali ke percakapan pengguna",
	breadcrumbRoot: "Log Percakapan",
	profileVerified: "Terverifikasi",
	profileUnverified: "Belum terverifikasi",
	profileAssistantAllowed: "use-ai-assistant",
	profileAssistantBlocked: "Tanpa izin asisten",
	searchConversation: "Cari judul percakapan…",
	conversationsTitle: "Percakapan",
	conversationsEmpty: "Belum ada percakapan pada rentang dan filter ini.",
	conversationMeta: (date, from, to, messages) =>
		`${date} · ${from} – ${to} WITA · ${messages} pesan`,
	conversationVoice: "🎙 Suara",
	conversationErrors: (n) => `${n} error`,
	openConversation: "Buka transkrip percakapan ini",
	loadMore: "Muat lebih banyak",
	userNotFound: "Pengguna tidak ditemukan.",
	loadFailed: "Data tidak dapat dimuat.",
	loading: "Memuat…",
	transcriptMeta: (date, from, to, messages, models, errors) =>
		`${date} · ${from} – ${to} WITA · ${messages} pesan · ${models} model · ${errors} error`,
	auditBanner:
		"Anda membuka isi percakapan pengguna lain. Akses ini dicatat di Audit Log (admin · waktu · id percakapan).",
	hideContent: "Sembunyikan isi",
	showContent: "Tampilkan isi",
	contentHiddenNote:
		"Isi pesan disembunyikan. Metadata giliran (model, token, latensi, tool) tetap ditampilkan.",
	retentionNote: (days) =>
		`Isi percakapan hanya disimpan ${days} hari terakhir. Lebih tua dari itu dihapus permanen oleh job retensi harian; angka pemakaian & biaya tetap tersimpan.`,
	askedFrom: (route) => `ditanya dari ${route}`,
	failoverChip: "⚠ Dialihkan ke Cadangan",
	errorTurn: (reason) => `Gagal dijawab — ${reason}`,
	errorTurnDefault: "penyedia tidak memberi jawaban.",
	errorTurnNote:
		"Giliran ini tidak menghabiskan token dan tidak dihitung ke plafon.",
	loadPrevious: (shown, total) =>
		`Menampilkan ${shown} dari ${total} pesan · Muat pesan sebelumnya`,
	transcriptStart: "Awal percakapan.",
	infoTitle: "Info percakapan",
	infoUser: "Pengguna",
	infoCreated: "Dibuat",
	infoLastMessage: "Pesan terakhir",
	infoMessages: "Jumlah pesan",
	infoTurns: "Giliran ke penyedia",
	infoModalities: "Modalitas",
	infoPageRoutes: "Halaman asal",
	infoModels: "Model yang dipakai",
	infoTools: "Sumber data dipakai",
	infoTokens: "Total token",
	infoCost: "Estimasi biaya",
	openAuditLog: "Buka di Audit Log",
	turns: (n) => `${n} giliran`,
	uses: (n) => `${n}×`,
	none: "—",
	transcriptNotFound: "Percakapan tidak ditemukan.",
	transcriptForbidden:
		"Anda tidak punya izin membaca isi percakapan. Minta admin menyalakan izin transkrip di Role & Permission.",
};

const en: AssistantLogDetailText = {
	backToUsers: "‹ Back to user list",
	backToUser: "‹ Back to user conversations",
	breadcrumbRoot: "Conversation Log",
	profileVerified: "Verified",
	profileUnverified: "Not verified",
	profileAssistantAllowed: "use-ai-assistant",
	profileAssistantBlocked: "No assistant permission",
	searchConversation: "Search conversation title…",
	conversationsTitle: "Conversations",
	conversationsEmpty: "No conversations in this range and filter yet.",
	conversationMeta: (date, from, to, messages) =>
		`${date} · ${from} – ${to} WITA · ${messages} messages`,
	conversationVoice: "🎙 Voice",
	conversationErrors: (n) => `${n} errors`,
	openConversation: "Open this conversation transcript",
	loadMore: "Load more",
	userNotFound: "User not found.",
	loadFailed: "The data could not be loaded.",
	loading: "Loading…",
	transcriptMeta: (date, from, to, messages, models, errors) =>
		`${date} · ${from} – ${to} WITA · ${messages} messages · ${models} models · ${errors} errors`,
	auditBanner:
		"You are opening another user's conversation content. This access is recorded in the Audit Log (admin · time · conversation id).",
	hideContent: "Hide content",
	showContent: "Show content",
	contentHiddenNote:
		"Message content is hidden. Turn metadata (model, tokens, latency, tools) is still shown.",
	retentionNote: (days) =>
		`Conversation content is only kept for the last ${days} days. Anything older is permanently deleted by the daily retention job; usage and cost figures are kept.`,
	askedFrom: (route) => `asked from ${route}`,
	failoverChip: "⚠ Switched to Backup",
	errorTurn: (reason) => `Failed to answer — ${reason}`,
	errorTurnDefault: "the provider returned no answer.",
	errorTurnNote: "This turn used no tokens and does not count toward the cap.",
	loadPrevious: (shown, total) =>
		`Showing ${shown} of ${total} messages · Load earlier messages`,
	transcriptStart: "Start of conversation.",
	infoTitle: "Conversation info",
	infoUser: "User",
	infoCreated: "Created",
	infoLastMessage: "Last message",
	infoMessages: "Message count",
	infoTurns: "Provider turns",
	infoModalities: "Modalities",
	infoPageRoutes: "Origin pages",
	infoModels: "Models used",
	infoTools: "Data sources used",
	infoTokens: "Total tokens",
	infoCost: "Estimated cost",
	openAuditLog: "Open in Audit Log",
	turns: (n) => `${n} turns`,
	uses: (n) => `${n}×`,
	none: "—",
	transcriptNotFound: "Conversation not found.",
	transcriptForbidden:
		"You do not have permission to read conversation content. Ask an admin to enable the transcript permission in Role & Permission.",
};

export const assistantLogDetailTexts = { id, en } as const;
